import React, { createContext, useState, useEffect, useContext, useRef } from "react";
import BusinessContext from "./business-context";
import { refreshToken, withActiveToken } from "../lib/auth";
import { useRouter } from "next/router";
import { repoUrl, pegPath, RepoRequestError, readJson } from "../lib/repo";

const PegsContext = createContext({
    handleCreatePeg: () => { },
    getPegs: () => { },
    pegs: null,
    deletePeg: () => { },
    getPeg: () => { },
    getPegReview: () => Promise.reject(new Error('PegsProvider missing')),
    postPegMessage: () => Promise.reject(new Error('PegsProvider missing')),
    resubmitPeg: () => Promise.reject(new Error('PegsProvider missing')),
    updatePegInList: () => { },
    generateToken: () => { },
    getTokens: () => { },
    deleteToken: () => { },
    checkPegAvalability: () => { },
    onCreatePeg: () => { },
    createdToken: null,
    setCreatedToken: () => { },
    getVersions: () => { },
    getfunctionVersions: () => { },
    activeVersion: null,
    setActiveVersion: () => { },
});

export const PegsProvider = (props) => {
    const router = useRouter();
    const { setNotificationContent } = useContext(BusinessContext);
    const [pegs, setPegs] = useState(null);
    const [pegsLoadFailed, setPegsLoadFailed] = useState(false);
    const [activeVersion, setActiveVersion] = useState(null);
    const [createdToken, setCreatedToken] = useState(null);

    const businessUuid = router.query.uuid

    const checkPegAvalability = async (peg, callback, onError) => {
        try {
            const response = await fetch(repoUrl(`/api/pegs/?peg_id=${encodeURIComponent(peg)}`), {
                headers: {
                    'X-BUSINESS': businessUuid
                }
            })
            if (!response.ok) throw new Error('Failed to check peg availability')
            const data = await response.json()
            callback(data)
        } catch (error) {
            console.error(error)
            onError && onError();
        }
    }

    const onCreatePeg = async (peg, callback, onError) => {
        withActiveToken(async () => {
            try {
                const response = await fetch(repoUrl(`/api/pegs/`), {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'X-BUSINESS': businessUuid,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ id: peg.urn, name: peg.name, description: peg.description })
                })
                // The status reaches onError: a 409 means the URN is taken,
                // which the availability check can't always see.
                if (!response.ok) throw new RepoRequestError(response.status, await readJson(response))
                const data = await response.json()
                callback(data)
            } catch (error) {
                console.error(error)
                onError && onError(error);
            }
        }, router)
    }

    // The business the page is on now. A list request that resolves after
    // the developer switched business must not overwrite the new one's pegs.
    const businessRef = useRef(businessUuid);
    businessRef.current = businessUuid;

    useEffect(() => {
        setPegs(null);
        setPegsLoadFailed(false);
    }, [businessUuid]);

    /**
     * Any repo call made with the developer's session: the access cookie
     * plus X-BUSINESS, after `withActiveToken` has refreshed an expired
     * token. A 401 anyway (the cookie outlived its own expiry stamp) gets
     * one refresh and one retry. Resolves with the parsed body; rejects with
     * a RepoRequestError for a non-2xx answer, or the fetch error.
     */
    const requestRepo = (path, { method = 'GET', body } = {}) => new Promise((resolve, reject) => {
        const send = () => fetch(repoUrl(path), {
            method,
            credentials: 'include',
            headers: {
                'X-BUSINESS': businessUuid,
                ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
            },
            body: body !== undefined ? JSON.stringify(body) : undefined,
        });
        const run = async () => {
            let response = await send();
            if (response.status === 401) {
                try {
                    await refreshToken();
                } catch (error) {
                    throw new RepoRequestError(401, null);
                }
                response = await send();
            }
            const data = await readJson(response);
            if (!response.ok) throw new RepoRequestError(response.status, data);
            return data;
        };
        withActiveToken(() => { run().then(resolve, reject); }, router);
    });

    /**
     * "Your Pegs" and the dashboard: this business's own pegs, in every
     * review state, from the owner-scoped list. (The public catalogue at
     * /api/pegs/ lists every developer's pegs and is only for the URN
     * availability check.)
     */
    const getPegs = async () => {
        const forBusiness = businessUuid;
        try {
            const data = await requestRepo('/api/developers/pegs/')
            const list = Array.isArray(data) ? data : data?.results
            if (!Array.isArray(list)) throw new Error('Unexpected pegs response')
            if (businessRef.current !== forBusiness) return
            setPegs(list)
            setPegsLoadFailed(false)
        } catch (error) {
            console.error(error)
            if (businessRef.current !== forBusiness) return
            setPegsLoadFailed(true)
        }
    }

    // Keeps a loaded list in step after a peg's review changed here.
    const updatePegInList = (peg) => {
        if (!peg?.id) return;
        setPegs((prev) => (prev ? prev.map((p) => (p.id === peg.id ? { ...p, ...peg } : p)) : prev));
    }

    /** `{ peg, events, can_resubmit, can_comment }` — rejects with RepoRequestError. */
    const getPegReview = (pegId) => requestRepo(`/api/pegs/${pegPath(pegId)}/review/`);

    /** Posts a message to the reviewers; resolves with the new event. */
    const postPegMessage = (pegId, body) => (
        requestRepo(`/api/pegs/${pegPath(pegId)}/review/messages/`, { method: 'POST', body: { body } })
    );

    /**
     * Edit-and-resubmit: `{ expected_revision, name?, description?, note? }`.
     * Resolves with `{ peg, events, ... }` and updates the loaded list.
     */
    const resubmitPeg = async (pegId, payload) => {
        const data = await requestRepo(`/api/pegs/${pegPath(pegId)}/review/resubmit/`, { method: 'POST', body: payload });
        updatePegInList(data?.peg);
        return data;
    };

    const handleCreatePeg = (peg, callback, onError) => {

        const createPegCallback = (json) => {
            callback(json);
            getPegs();
        }
        refreshToken((token) => { onCreatePeg(peg, createPegCallback, onError) })
            .catch((error) => {
                // A failed refresh used to leave the button spinning forever.
                console.error(error);
                onError && onError(error);
            })
    }

    const getPeg = async (pegId, callback, onError) => {
        try {
            const response = await fetch(repoUrl(`/api/pegs/${pegPath(pegId)}/`), {
                headers: {
                    'X-BUSINESS': businessUuid
                }
            })
            if (!response.ok) throw new Error('Failed to fetch peg')
            const data = await response.json()
            callback(data)
        } catch (error) {
            console.error(error)
            onError && onError(error)
        }
    }

    const deletePeg = async (pegId, callback) => {
        withActiveToken(async () => {
            try {
                const response = await fetch(repoUrl(`/api/pegs/${pegPath(pegId)}/`), {
                    method: 'DELETE',
                    credentials: 'include',
                    headers: {
                        'X-BUSINESS': businessUuid,
                    }
                })
                if (!response.ok) throw new Error('Failed to delete peg')
                callback()
                setPegs(pegs.filter(peg => peg.id !== pegId))
            } catch (error) {
                console.error(error)
            }
        }, router)
    }

    const generateToken = async (pegId, tokenTag, callback, onError) => {

        const payload = {
            tag: tokenTag,
            ttl: 31536000,
            peg_id: pegId,
        }

        withActiveToken(async () => {
            try {
                const response = await fetch(repoUrl(`/api/pegs/${pegPath(pegId)}/keys/`), {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'X-BUSINESS': businessUuid,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(payload)
                })
                if (!response.ok) throw new Error('Failed to generate token')
                const data = await response.json()
                callback(data)
            } catch (error) {
                console.error(error)
                setNotificationContent({
                    showNotification: true,
                    header: 'Something went wrong',
                    message: "We couldn't generate a token. Please try again.",
                    icon: 'alert-triangle',
                });
                onError && onError(error);
            }
        }, router)
    }

    const getTokens = async (pegId, callback, onError) => {
        try {
            const response = await fetch(repoUrl(`/api/pegs/${pegPath(pegId)}/keys/`), {
                method: 'GET',
                headers: {
                    'X-BUSINESS': businessUuid,
                },
                credentials: 'include',
            })
            if (!response.ok) throw new Error('Failed to fetch tokens')
            const data = await response.json()
            callback(data)
        } catch (error) {
            onError && onError();
            console.error(error)
        }
    }

    const getfunctionVersions = async (pegId, branchId, callback, onError) => {
        try {
            const response = await fetch(repoUrl(`/api/pegs/${pegPath(pegId)}/functions/${branchId}/`), {
                method: 'GET',
                headers: {
                    'X-BUSINESS': businessUuid,
                },
                credentials: 'include',
            })
            if (!response.ok) throw new Error(`Failed to fetch versions for branch ${branchId}`)
            const data = await response.json()
            callback(data)
        } catch (error) {
            console.error(error)
            onError && onError()
        }

    }

    const getVersions = async (pegId, callback, onError) => {
        try {
            const response = await fetch(repoUrl(`/api/pegs/${pegPath(pegId)}/functions/`), {
                method: 'GET',
                headers: {
                    'X-BUSINESS': businessUuid,
                },
                credentials: 'include',
            })
            if (!response.ok) throw new Error('Failed to fetch functions')
            const data = await response.json()
            const functions = data.functions || []
            if (functions.length === 0) {
                onError && onError();
                return;
            }
            await Promise.all(functions.map(fn => getfunctionVersions(pegId, fn, callback, onError)))
        } catch (error) {
            console.error(error)
            onError && onError()
        }
    }

    const deleteToken = async (pegId, tokenId, callback) => {
        withActiveToken(async () => {
            try {
                const response = await fetch(repoUrl(`/api/pegs/${pegPath(pegId)}/keys/${tokenId}/`), {
                    method: 'DELETE',
                    credentials: 'include',
                    headers: {
                        'X-BUSINESS': businessUuid,
                    }
                })
                if (!response.ok) throw new Error('Failed to delete token')
                callback()
            } catch (error) {
                console.error(error)
            }
        }, router)
    }

    
    const context = {
        handleCreatePeg,
        getPegs,
        pegs,
        pegsLoadFailed,
        deletePeg,
        getPeg,
        getPegReview,
        postPegMessage,
        resubmitPeg,
        updatePegInList,
        generateToken,
        getTokens,
        deleteToken,
        checkPegAvalability,
        onCreatePeg,
        setCreatedToken,
        createdToken,
        getVersions,
        getfunctionVersions,
        activeVersion,
        setActiveVersion,
    };

    return (
        <PegsContext.Provider value={context}>
            {props.children}
        </PegsContext.Provider>
    );
};

export default PegsContext;
